import axios from 'axios';
import { tokenRefreshManager } from './tokenRefreshManager';
import { tokenStorage } from './secureStorage';
import type { AuthResponseDto, RefreshRequestDto } from '../types/user';

const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? 'http://localhost:5019/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

/** Returns true when the JWT access token is missing or past its expiry. */
export function isAccessTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
    if (typeof payload.exp !== 'number') return true;
    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

/**
 * Refreshes tokens using a single in-flight promise so concurrent callers
 * (ProtectedRoute, axios interceptor, etc.) never race on token rotation.
 */
export async function refreshAccessToken(refreshTokenOverride?: string): Promise<AuthResponseDto> {
  const refreshToken = refreshTokenOverride ?? tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  return tokenRefreshManager.refresh(async () => {
    const request: RefreshRequestDto = { refreshToken };
    const { data } = await authApi.post<AuthResponseDto>('/auth/refresh', request);
    tokenStorage.setAccessToken(data.accessToken);
    tokenStorage.setRefreshToken(data.refreshToken);
    return data;
  });
}
